import { motion } from 'framer-motion'
import { Plug, MessageSquare, Cpu, ShieldCheck } from 'lucide-react'

const steps = [
  {
    Icon: Plug,
    label: 'Connect',
    title: 'Wire up your stack',
    body: 'Add Jira, GitHub, Postgres, Slack, Confluence — whatever you use. Credentials encrypted with per-tenant keys; no secrets in plaintext, ever.',
    cmd: '$ testops connect jira github postgres',
    out: 'connected: 3 providers',
    color: 'from-cyan-500 to-sky-500',
    glow: 'bg-cyan-500/20',
  },
  {
    Icon: MessageSquare,
    label: 'Ask',
    title: 'Type one command',
    body: 'Plain English. The agent decides which tools to call across all twelve integrations and your test platforms — function-calling, not regex.',
    cmd: '> show failing tests this week, link tickets',
    out: 'planning across 5 tools…',
    color: 'from-sky-500 to-blue-500',
    glow: 'bg-sky-500/20',
  },
  {
    Icon: Cpu,
    label: 'Orchestrate',
    title: 'Watch it execute',
    body: 'Multi-step planning streams in real time. Every tool call is logged with timing, args, and results. You see exactly what the agent did and why.',
    cmd: '▸ jira_search → 12  ▸ athena_runs → 8  ▸ github_diff → 3',
    out: 'analyzed in 2.4s',
    color: 'from-blue-500 to-cyan-500',
    glow: 'bg-blue-500/20',
  },
  {
    Icon: ShieldCheck,
    label: 'Approve',
    title: 'Consent on every write',
    body: 'Destructive actions pause for explicit approval — single-shot nonces bound to (conversation, tool, args). Every approval lands in an immutable audit log.',
    cmd: '⚠ pending: file 3 Jira bugs in KAN',
    out: '[approved by manoj@acme]',
    color: 'from-teal-500 to-cyan-600',
    glow: 'bg-teal-500/20',
  },
]

export function HowItWorks() {
  return (
    <section id="how-it-works" className="relative bg-zinc-950 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="font-mono text-xs uppercase tracking-widest text-cyan-400">
            // how it works
          </p>
          <h2 className="mt-4 text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Connect. Ask. Orchestrate.
          </h2>
          <p className="mt-4 text-lg text-white/70">
            Four steps from sign-in to running real ops across your stack.
          </p>
        </motion.div>

        <div className="mx-auto mt-20 max-w-5xl space-y-6">
          {steps.map((step, i) => (
            <motion.div
              key={step.label}
              initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="group relative grid items-center gap-6 rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition hover:border-white/20 hover:bg-white/[0.04] sm:grid-cols-12 sm:p-8"
            >
              {/* Step number + icon */}
              <div className="relative sm:col-span-3">
                <div className={`absolute -inset-2 rounded-2xl ${step.glow} blur-xl opacity-60 transition group-hover:opacity-100`} />
                <div className="relative flex items-center gap-4">
                  <div
                    className={`flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${step.color} shadow-lg`}
                  >
                    <step.Icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-widest text-white/60">
                      step {String(i + 1).padStart(2, '0')}
                    </p>
                    <p className="mt-0.5 font-semibold text-white">{step.label}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="sm:col-span-4">
                <h3 className="text-xl font-semibold text-white">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{step.body}</p>
              </div>

              {/* Terminal preview */}
              <div className="sm:col-span-5">
                <div className="rounded-lg border border-white/10 bg-black/40 p-4 font-mono text-xs">
                  <div className="text-white/80">{step.cmd}</div>
                  <div className="mt-1.5 text-emerald-300">↳ {step.out}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
